import { useState, useRef, useCallback, useEffect } from 'react';
import { styled } from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';

// Components
import Schedule, { ScheduleImgSrc } from '../../Schedule';
import ChromeTV from '../../ChromeTV';
import Matchbox from '../../Matchbox';
import Envelope from '../../Envelope';
import { RecordPlayer } from '../../RecordPlayer';
import { LillyEmitter } from '../../LillyEmitter';
import { PlaylistSearchModal } from '../../PlaylistSearchModal';
import { MotionItem } from '../../MotionItem/MotionItem';
import { ContentRegistry } from './contentRegistry';
import type { ContentId } from './contentRegistry';

// Styles and config
import { PageContainer, ContentContainer, ShadowBackground, BackgroundSVG, InlineContentLayer, InlineModalContainer } from './TopDownPage.styles';
import { getTopDownItemsConfig, TopDownItemId } from './config';
import { useFocusController, SCATTER_MS } from './focusController';
import { scatterConfig } from './scatterConfig';
import { getFocusConfig } from './focusConfig';
import { parseFocusFromUrl, setFocusUrl, clearFocusUrl, navigateToFocus } from './focusLinks';

// Assets - bundled by Vite
import shoesImg from '../../../assets/optimized/shoes.webp';
import bowtieImg from '../../../assets/optimized/bowtie.webp';
import recordImg from '../../../assets/optimized/record.webp';
import lillyImg from '../../../assets/optimized/lilly.webp';
import hotelBrochureImg from '../../../assets/optimized/new-ps-hotels.webp';
import engagement120Img from '../../../assets/optimized/CatherineandChrisEngagement-120.webp';
import { engagementMedia } from '../../../assets/weddingImages';

// Critical above-the-fold assets - served from public/ for preloading via index.html
const shadowImg = '/upscaled-shadows.webp';
const chrisAmpCatherineSvg = '/chris_amp_catherine.svg';

// Utils and services
import { isMobile } from '../../../lib/deviceUtils';
import { useImagePreloader } from '../../../hooks/useImagePreloader';
import { fetchPlaylist, type PlaylistEntry } from '../../../lib/playlistService';

const AnimatedInlineModal = motion(InlineModalContainer);
// Scatter animation timing: items use 220ms CSS transition
// Modal content fades in/out with 300ms after scatter completes / before un-scatter
const inlineModalTransition = { duration: 0.3, ease: [0.22, 1, 0.36, 1] as const };

const RecordPlayerWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    /* Allow pointer events for interactive children */
    pointer-events: auto;
`;


/**
 * Top-down view page with draggable items
 * Displays various wedding-related items that can be dragged around
 */
export default function TopDownPage() {
    const [playlistModalOpen, setPlaylistModalOpen] = useState(false);
    const [playlist, setPlaylist] = useState<PlaylistEntry[]>([]);
    const [currentSong, setCurrentSong] = useState<PlaylistEntry | null>(null);
    const zCounterRef = useRef(100);
    const hasHandledInitialIdleRef = useRef(false);
    const bringToFront = () => ++zCounterRef.current;
    const mobile = isMobile();
    const SHOW_RECORD = false; // temp: hide record on landing

    // Lilly emitter state
    const [isLillyEmitting, setIsLillyEmitting] = useState(false);
    const [lillyOrigin, setLillyOrigin] = useState<{ x: number; y: number } | null>(null);
    const [lillyParticleWidth, setLillyParticleWidth] = useState(0);
    const lillyRef = useRef<HTMLDivElement | null>(null);

    const topDownItemsConfig = getTopDownItemsConfig(mobile);
    const focusConfig = getFocusConfig(mobile);

    // Initialize focus controller
    const {
        state: focusState,
        focusItem,
        dismiss,
        getItemPosition,
        getTargetPose,
        isItemFocused,
        isAnyFocus,
        onContentExitComplete,
    } = useFocusController({
        itemsConfig: topDownItemsConfig,
        scatterConfig,
        focusConfig,
    });

    // Deep-link: read focus from URL path on mount
    useEffect(() => {
        const initialFocusId = parseFocusFromUrl();
        if (initialFocusId && scatterConfig.has(initialFocusId)) {
            // Small delay to ensure layout is ready before triggering focus
            const timer = setTimeout(() => {
                focusItem(initialFocusId);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, []); // Only on mount

    // Deep-link: sync URL path when focus state changes
    useEffect(() => {
        if (focusState.phase === 'focused' && focusState.focusedId) {
            // Use pushState to create a history entry so browser back button works
            navigateToFocus(focusState.focusedId);
            return;
        }

        if (focusState.phase === 'idle') {
            const urlTarget = parseFocusFromUrl();

            // Preserve deep-link on initial mount, but clear for any later idle states
            if (!hasHandledInitialIdleRef.current) {
                hasHandledInitialIdleRef.current = true;
                if (urlTarget) {
                    return;
                }
            }

            clearFocusUrl();
        }
    }, [focusState.phase, focusState.focusedId]);

    // Deep-link: handle browser back/forward navigation
    useEffect(() => {
        const handlePopState = () => {
            const urlFocusId = parseFocusFromUrl();
            
            // If navigated to root and we're focused or unfocusing, dismiss
            if (!urlFocusId && (focusState.phase === 'focused' || focusState.phase === 'unfocusing')) {
                // Only dismiss if we're actually focused (not already dismissing)
                if (focusState.phase === 'focused') {
                    dismiss();
                }
                return;
            }
            
            // If navigated to different item, switch focus
            if (urlFocusId && urlFocusId !== focusState.focusedId && scatterConfig.has(urlFocusId)) {
                focusItem(urlFocusId);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [focusState.phase, focusState.focusedId, focusItem, dismiss]);

    // Playlist API URL - always use same-origin proxy endpoint
    const playlistApiUrl = '/api/playlist';
    const youtubeApiKey = import.meta.env.VITE_YOUTUBE_API_KEY || '';

    // Load playlist on mount
    useEffect(() => {
        const loadPlaylist = async () => {
            try {
                const entries = await fetchPlaylist(playlistApiUrl);
                console.log('[TopDownPage] Playlist loaded:', {
                    entryCount: entries.length,
                    entries: entries.map(e => ({
                        videoId: e.videoId,
                        youtubeUrl: e.youtubeUrl,
                        title: e.title,
                        artist: e.artist,
                    })),
                });
                setPlaylist(entries);
                // Set first entry as current song
                if (entries.length > 0) {
                    const firstSong = entries[0];
                    console.log('[TopDownPage] Setting current song:', {
                        videoId: firstSong.videoId,
                        youtubeUrl: firstSong.youtubeUrl,
                        title: firstSong.title,
                        artist: firstSong.artist,
                    });
                    setCurrentSong(firstSong);
                } else {
                    console.log('[TopDownPage] No songs in playlist');
                    setCurrentSong(null);
                }
            } catch (error) {
                console.error('Failed to load playlist:', error);
            }
        };

        loadPlaylist();
    }, [playlistApiUrl]);

    const handleSongAdded = useCallback(() => {
        // Reload playlist after adding a song
        fetchPlaylist(playlistApiUrl)
            .then((entries) => {
                console.log('[TopDownPage] Playlist reloaded after adding song:', {
                    entryCount: entries.length,
                    entries: entries.map(e => ({
                        videoId: e.videoId,
                        youtubeUrl: e.youtubeUrl,
                        title: e.title,
                    })),
                });
                setPlaylist(entries);
                if (entries.length > 0) {
                    // Always update to the latest song (last entry is newest)
                    const latestSong = entries[entries.length - 1];
                    console.log('[TopDownPage] Setting latest song as current:', {
                        videoId: latestSong.videoId,
                        youtubeUrl: latestSong.youtubeUrl,
                        title: latestSong.title,
                    });
                    setCurrentSong(latestSong);
                }
            })
            .catch((error) => {
                console.error('Failed to reload playlist:', error);
            });
    }, [playlistApiUrl]);
    // Item names for logging
    const itemNames: Record<TopDownItemId, string> = {
        [TopDownItemId.ChromeTV]: 'ChromeTV',
        [TopDownItemId.Schedule]: 'Schedule',
        [TopDownItemId.Matchbox]: 'Matchbox',
        [TopDownItemId.Envelope]: 'Envelope',
        [TopDownItemId.Shoes]: 'Shoes',
        [TopDownItemId.Bowtie]: 'Bowtie',
        [TopDownItemId.Lilly]: 'Lilly',
        [TopDownItemId.RecordPlayer]: 'RecordPlayer',
        [TopDownItemId.Hotels]: 'Postcard',
    };

    // Image sources for preloading
    const imageSources = [
        ScheduleImgSrc,
        shoesImg,
        bowtieImg,
        recordImg,
        lillyImg,
        hotelBrochureImg,
    ];

    useImagePreloader(imageSources);

    // Handle item press via controller orchestration (queue-aware)
    const handleItemPress = useCallback((id: TopDownItemId) => {
        focusItem(id);
    }, [focusItem]);

    // Simple content open flag for generic mode
    const isContentOpen = focusState.mode === 'generic' && !!focusState.contentId && focusState.phase !== 'idle';

    // Handle back button - use browser history navigation to match browser back button behavior
    const handleBack = useCallback(() => {
        // Always call dismiss directly to ensure the close button works
        // dismiss() will check internally if it should work (phase must be 'focused')
        // The URL will be cleared by the effect that watches focusState.phase
        // For browser back button, the popstate handler will call dismiss when URL changes
        dismiss();
    }, [dismiss]);

    // Lilly pointer event handlers for emitter
    const handleLillyPointerDown = useCallback((e: React.PointerEvent) => {
        // Only emit when in idle state (not focused)
        if (focusState.phase !== 'idle') {
            return;
        }

        const target = e.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();

        // Calculate center of the Lilly item
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        setLillyOrigin({ x: centerX, y: centerY });
        setLillyParticleWidth(rect.width * 1); // Particles are 40% of the Lilly size for better visual
        setIsLillyEmitting(true);
    }, [focusState.phase]);

    const handleLillyPointerEnd = useCallback(() => {
        setIsLillyEmitting(false);
    }, []);

    return (
        <PageContainer
            data-modal-open={isAnyFocus() ? 'true' : undefined}
            onClick={(e) => {
                // Global click-to-dismiss when focused
                if (isAnyFocus() && e.target === e.currentTarget) {
                    dismiss();
                }
            }}
        >
            {/* Shadow Background */}
            <ShadowBackground src={shadowImg} alt="" />
            <ContentContainer
                data-focused={isAnyFocus() ? 'true' : undefined}
                onClick={(e) => {
                    // Dismiss when clicking background (but not items)
                    if (isAnyFocus() && e.target === e.currentTarget) {
                        dismiss();
                    }
                }}
            >
                {/* Background SVG with fade */}
                <BackgroundSVG
                    src={chrisAmpCatherineSvg}
                    alt=""
                    data-background-svg
                    data-faded={focusState.phase !== 'idle' ? 'true' : undefined}
                />

                {/* Chrome TV */}
                {(() => {
                    const config = topDownItemsConfig.get(TopDownItemId.ChromeTV);
                    if (!config) return null;
                    const { id } = config;
                    const pose = getTargetPose(id);
                    const isChromeTVFocused = focusState.focusedId === TopDownItemId.ChromeTV &&
                        focusState.phase === 'focused' &&
                        focusState.contentId === 'tv-gallery';
                    return (
                        <MotionItem
                            id={id}
                            top={pose.top}
                            left={pose.left}
                            width={pose.width}
                            rotate={pose.rotate}
                            z={pose.z}
                            hoverEnabled={!mobile && focusState.phase === 'idle'}
                            transitionDurationMs={SCATTER_MS}
                            onPress={() => handleItemPress(id)}
                            clickable={true}
                        >
                            <ChromeTV
                                media={engagementMedia}
                                autoPlay={!isChromeTVFocused}
                                showStatic={isChromeTVFocused ? true : undefined}
                                transitionDuration={
                                    focusState.phase === 'focusing' || focusState.phase === 'unfocusing'
                                        ? 0
                                        : 0
                                }
                            />
                        </MotionItem>
                    );
                })()}

                {/* Schedule */}
                {(() => {
                    const config = topDownItemsConfig.get(TopDownItemId.Schedule);
                    if (!config) return null;
                    const { id } = config;
                    const pose = getTargetPose(id);
                    return (
                        <MotionItem
                            id={id}
                            top={pose.top}
                            left={pose.left}
                            width={pose.width}
                            rotate={pose.rotate}
                            z={pose.z}
                            hoverEnabled={!mobile && focusState.phase === 'idle'}
                            transitionDurationMs={SCATTER_MS}
                            alt="Schedule"
                            onPress={() => handleItemPress(id)}
                            clickable={true}
                        >
                            <Schedule />
                        </MotionItem>
                    );
                })()}

                {/* Matchbox */}
                {(() => {
                    const config = topDownItemsConfig.get(TopDownItemId.Matchbox);
                    // if (!config) return null;
                    const { id } = config;
                    const pose = getTargetPose(id);
                    return (
                        <MotionItem
                            id={id}
                            top={pose.top}
                            left={pose.left}
                            width={pose.width}
                            rotate={pose.rotate}
                            z={pose.z}
                            hoverEnabled={!mobile && focusState.phase === 'idle'}
                            transitionDurationMs={SCATTER_MS}
                            onPress={() => handleItemPress(id)}
                            clickable={true}
                        >
                            <Matchbox />
                        </MotionItem>
                    );
                })()}

                {/* Envelope */}
                {(() => {
                    const config = topDownItemsConfig.get(TopDownItemId.Envelope);
                    if (!config) return null;
                    const { id } = config;
                    const pose = getTargetPose(id);
                    return (
                        <MotionItem
                            id={id}
                            top={pose.top}
                            left={pose.left}
                            width={pose.width}
                            rotate={pose.rotate}
                            z={pose.z}
                            hoverEnabled={!mobile && focusState.phase === 'idle'}
                            transitionDurationMs={SCATTER_MS}
                            onPress={() => handleItemPress(id)}
                            clickable={true}
                        >
                            <div data-allow-pointer-events="true">
                                <Envelope onClick={() => handleItemPress(id)} />
                            </div>
                        </MotionItem>
                    );
                })()}

                {/* Bowtie */}
                {(() => {
                    const config = topDownItemsConfig.get(TopDownItemId.Bowtie);
                    if (!config) return null;
                    const { id } = config;
                    const pose = getTargetPose(id);
                    // const zAboveOverlay = focusState.contentId === 'dresscode' ? 500 : config.z;
                    return (
                        <MotionItem
                            id={id}
                            top={pose.top}
                            left={pose.left}
                            width={pose.width}
                            rotate={pose.rotate}
                            z={pose.z}
                            src={bowtieImg}
                            alt="Bowtie"
                            hoverEnabled={!mobile && focusState.phase === 'idle'}
                            transitionDurationMs={SCATTER_MS}
                            onPress={() => handleItemPress(id)}
                            clickable={true}
                        />
                    );
                })()}

                {/* Shoes */}
                {(() => {
                    const config = topDownItemsConfig.get(TopDownItemId.Shoes);
                    if (!config) return null;
                    const { id } = config;
                    const pose = getTargetPose(id);
                    const zAboveOverlay = focusState.contentId === 'dresscode' ? 500 : config.z;
                    return (
                        <MotionItem
                            id={id}
                            top={pose.top}
                            left={pose.left}
                            width={pose.width}
                            rotate={pose.rotate}
                            z={zAboveOverlay}
                            src={shoesImg}
                            alt="Shoes"
                            hoverEnabled={!mobile && focusState.phase === 'idle'}
                            transitionDurationMs={SCATTER_MS}
                            onPress={() => handleItemPress(id)}
                            clickable={true}
                        />
                    );
                })()}

                {/* Lilly */}
                {(() => {
                    const config = topDownItemsConfig.get(TopDownItemId.Lilly);
                    if (!config) return null;
                    const { id } = config;
                    const pose = getTargetPose(id);
                    return (
                        <MotionItem
                            id={id}
                            top={pose.top}
                            left={pose.left}
                            width={pose.width}
                            rotate={pose.rotate}
                            z={pose.z}
                            src={lillyImg}
                            alt="Lilly"
                            hoverEnabled={!mobile && focusState.phase === 'idle'}
                            transitionDurationMs={SCATTER_MS}
                            onPress={() => {
                                if (isAnyFocus()) {
                                    handleItemPress(id);
                                } else {
                                    console.log(itemNames[TopDownItemId.Lilly]);
                                }
                            }}
                            clickable={true}
                            onPointerDown={handleLillyPointerDown}
                            onPointerUp={handleLillyPointerEnd}
                            onPointerCancel={handleLillyPointerEnd}
                            onPointerLeave={handleLillyPointerEnd}
                        />
                    );
                })()}

                {/* RecordPlayer (temporarily hidden) */}
                {SHOW_RECORD && (() => {
                    const config = topDownItemsConfig.get(TopDownItemId.RecordPlayer);
                    if (!config) return null;
                    const { id } = config;
                    const pose = getTargetPose(id);
                    return (
                        <MotionItem
                            id={id}
                            top={pose.top}
                            left={pose.left}
                            width={pose.width}
                            rotate={pose.rotate}
                            z={pose.z}
                            hoverEnabled={!mobile && focusState.phase === 'idle'}
                            transitionDurationMs={SCATTER_MS}
                            onPress={() => {
                                if (isAnyFocus()) {
                                    handleItemPress(id);
                                } else {
                                    console.log(itemNames[TopDownItemId.RecordPlayer]);
                                }
                            }}
                            clickable={true}
                        >
                            <RecordPlayerWrapper data-allow-pointer-events="true">
                                <RecordPlayer
                                    labelSrc={engagement120Img}
                                // youtubeUrl={currentSong?.youtubeUrl}
                                // youtubeVideoId={currentSong?.videoId}
                                // songInfo={{
                                //     title: currentSong?.title,
                                //     artist: currentSong?.artist,
                                // }}
                                />
                                {/* Debug info in dev */}
                                {process.env.NODE_ENV === 'development' && currentSong && (
                                    <div style={{
                                        position: 'absolute',
                                        bottom: '-30px',
                                        left: '0',
                                        fontSize: '10px',
                                        color: '#fff',
                                        background: 'rgba(0,0,0,0.7)',
                                        padding: '2px 4px',
                                        whiteSpace: 'nowrap',
                                        pointerEvents: 'none',
                                    }}>
                                        {currentSong.videoId ? `Video: ${currentSong.videoId}` : 'No video ID'}
                                    </div>
                                )}
                            </RecordPlayerWrapper>
                        </MotionItem>
                    );
                })()}

                {/* Postcard */}
                {(() => {
                    const config = topDownItemsConfig.get(TopDownItemId.Hotels);
                    if (!config) return null;
                    const { id } = config;
                    const pose = getTargetPose(id);
                    return (
                        <MotionItem
                            id={id}
                            top={pose.top}
                            left={pose.left}
                            width={pose.width}
                            rotate={pose.rotate}
                            z={config.z}
                            src={hotelBrochureImg}
                            alt="Postcard"
                            hoverEnabled={!mobile && focusState.phase === 'idle'}
                            transitionDurationMs={SCATTER_MS}
                            onPress={() => handleItemPress(id)}
                            clickable={true}
                        />
                    );
                })()}

                {/* Unified Generic Content Modal */}
                {focusState.mode === 'generic' && focusState.contentId && (
                    <InlineContentLayer onClick={dismiss}>
                        <AnimatePresence mode="wait" onExitComplete={onContentExitComplete}>
                            {focusState.overlayMounted && focusState.overlayVisible && (
                                <AnimatedInlineModal
                                    key={`content-${focusState.contentId}`}
                                    $isMobile={mobile}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={inlineModalTransition}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <ContentRegistry
                                        contentId={focusState.contentId as ContentId}
                                        onClose={handleBack}
                                        isScattering={focusState.phase === 'focusing' || focusState.phase === 'unfocusing'}
                                    />
                                </AnimatedInlineModal>
                            )}
                        </AnimatePresence>
                    </InlineContentLayer>
                )}
            </ContentContainer>

            <PlaylistSearchModal
                open={playlistModalOpen}
                onClose={() => setPlaylistModalOpen(false)}
                playlistApiUrl={playlistApiUrl}
                youtubeApiKey={youtubeApiKey}
                onSongAdded={handleSongAdded}
            />

            {/* Lilly particle emitter overlay */}
            <LillyEmitter
                isEmitting={isLillyEmitting}
                origin={lillyOrigin}
                src={lillyImg}
                particleWidth={lillyParticleWidth}
            />
        </PageContainer>
    );
}

